<template>
  <div>
    <div class="space-y-4">
      <cashbill-summary-widget :with-style="false" :meta="summary" :with-omzet="filter.is_omzet" />
      <cashbill-status-summary :with-style="false" :status-summary="statusSummary" />

      <div class="flex justify-between items-center">
        <div class="flex items-start gap-2">
          <base-checkbox
            label="Omzet"
            name="omzet"
            v-model="filter.is_omzet"
            @change="loadOrders"
          />
          <p class="mt-1" title="Omzet adalah faktur yang sudah divalidasi kasir dan faktur tambah point yang sudah divalidasi kasir di periode berikutnya">
            <Icon icon="heroicons:information-circle-solid" class="size-4 text-blue-600" />
          </p>
        </div>
        <div class="flex items-center gap-2">
          <base-select
            v-if="!filter.is_omzet"
            :shadow="false"
            :options="filterStatusOptions"
            expand
            v-model="filter.status"
            @change="loadOrders"
          />
          <mitra-period-select
            v-model="filter.mitra_period_id"
            @change="loadOrders"
          />
        </div>
      </div>
      <datatable
        :columns="headers"
        :total="orders.meta.page.total"
        :perPage="orders.meta.page.perPage"
        :meta="orders.meta"
        :currentPage="orders.meta.page.currentPage"
        @pagechanged="onPageChanged"
      >
        <template v-slot:tbody="{ classes }">
          <tbody :class="classes.tbody">
            <tr
              v-for="(order, index) in orders.data"
              :key="order.id"
              :class="[
                classes.tr,
                index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                'cursor-pointer bg-white hover:bg-green-100',
              ]"
              @click="onDetail(order)"
            >
              <td :class="[classes.td]" v-if="columns.includes('code')">
                <span class="font-bold text-gray-900"
                  >{{ order.attributes.origin_code }} /
                  {{ order.attributes.destination_code }}</span
                >
              </td>
              <td :class="classes.td" v-if="columns.includes('date')">
                {{ order.attributes.createdAt | formatDate('ll') }}
              </td>
              <td
                :class="[classes.td, 'text-right']"
                v-if="columns.includes('total_pv')"
              >
                {{
                  getSingleIncluded(orders, order.relationships.order.data.id)
                    .attributes.grand_total_pv | toCurrency
                }}
              </td>
              <td
                :class="[classes.td, 'text-right']"
                v-if="columns.includes('total_bv')"
              >
                {{
                  (getSingleIncluded(orders, order.relationships.order.data.id)
                    .attributes.grand_total_bv ?? 0) | toCurrency
                }}
              </td>
              <td :class="classes.td" v-if="columns.includes('stockist_code')">
                {{
                  getSingleIncluded(orders, order.relationships.order.data.id)
                    .attributes.origin_office_code
                }}
              </td>
              <td :class="[classes.td, 'text-center']">
                <base-badge color="indigo">{{
                  order.attributes.order_status
                }}</base-badge>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr :class="[classes.tr]">
              <td :class="[classes.td]" colspan="2">Total</td>
              <td :class="[classes.td, 'text-right']">
                {{ summaryOrder.total_pv | toCurrency }}
              </td>
              <td :class="[classes.td, 'text-right']">
                {{ summaryOrder.total_bv | toCurrency }}
              </td>
            </tr>
          </tfoot>
        </template>
      </datatable>
    </div>
    <member-purchase-order-detail-modal
      :visible="detailModal.visible"
      :order-id="detailModal.orderId"
      @close="detailModal.visible = false"
    />
    <loading v-if="loadingOrders || this.loadingSummaryOrder" />
  </div>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import { mapGetters } from 'vuex';
import MemberPurchaseOrderDetailModal from './member-purchase-order-detail-modal.vue';
import MitraPeriodSelect from '@/components/period/mitra-period/mitra-period-select.vue';
import BaseCheckbox from '../base/BaseCheckbox.vue';
import CashbillSummaryWidget from '../cash-bill/cashbill-summary-widget.vue';
import CashbillStatusSummary from '../cash-bill/cashbill-status-summary.vue';

export default {
  mixins: [requestMixin],
  components: {
    MemberPurchaseOrderDetailModal,
    MitraPeriodSelect,
    BaseCheckbox,
    CashbillSummaryWidget,
    CashbillStatusSummary
  },
  props: {
    columns: Array,
  },
  data() {
    return {
      orders: {
        data: [],
        meta: {
          page: {
            total: 0,
            perPage: 10,
            currentPage: 1,
          },
        },
        include: [],
      },
      summaryOrder: {
        total_pv: 0,
        total_bv: 0,
      },
      filter: {
        mitra_period_id: null,
        is_omzet: false,
        status: null
      },
      loadingOrders: false,
      loadingSummaryOrder: false,
      detailModal: {
        visible: false,
        orderId: null,
      },
      summary: {
        totalPrice: 0,
        totalPv: 0,
        totalBv: 0,
        totalOmzet: 0,
        count: 0
      },
      statusSummary: {
        draft: 0,
        not_paid: 0,
        not_packed: 0,
        not_shipped: 0,
        not_taken: 0,
        shipped: 0,
        taken: 0
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    headers() {
      return [
        { id: 'code', name: 'NMRBLL' },
        { id: 'date', name: 'TGLBLL' },
        { id: 'total_pv', name: 'PV', theadClass: 'text-right' },
        { id: 'total_bv', name: 'BV', theadClass: 'text-right' },
        { id: 'stockist_code', name: 'KODEST' },
        { id: 'status', name: 'Status', theadClass: 'text-center' },
      ].filter((header) => this.columns.includes(header.id));
    },
    filterStatusOptions() {
      return [
        {
          key: 'null',
          value: null,
          label: 'Semua',
        },
        {
          key: 'true',
          value: true,
          label: 'Valid',
        },
        {
          key: 'reported',
          value: 'reported',
          label: 'Dilaporkan',
        },
        {
          key: 'false',
          value: false,
          label: 'Draft',
        },
        {
          key: 'cancel',
          value: 'cancel',
          label: 'Cancel',
        },
        {
          key: 'deleted',
          value: 'deleted',
          label: 'Hapus',
        },
      ];
    },
    filterStatus() {
      if (this.filter.is_omzet) {
        return {
          'filter[not-order_status]': 'Draft,Diproses Kasir',
        };
      }

      if (this.filter.status === null) {
        return {};
      }

      if (this.filter.status === 'deleted') {
        return {
          'filter[only-trahsed]': true,
        };
      }

      if (this.filter.status === 'reported') {
        return {
          'filter[order_status]': 'Bonus Terlaporkan',
        };
      }

      if (this.filter.status === 'cancel') {
        return {
          'filter[order_status]': 'Batal Faktur Gudang,Batal Faktur Kasir',
        };
      }

      if (this.filter.status) {
        return {
          'filter[not-order_status]': 'Draft',
        };
      }

      return {
        'filter[order_status]': 'Draft',
      };
    },
  },
  methods: {
    onPageChanged(page) {
      this.loadOrders({ 'page[number]': page }, { withSummary: false });
    },
    onDetail(order) {
      this.detailModal.orderId = order.relationships.order.data.id;
      this.detailModal.visible = true;
    },
    async loadOrders(params = {}, { withSummary } = { withSummary: true }) {
      this.loadingOrders = true;

      const [res, err] = await this.request('/api/v1/orders', {
        params: {
          'page[size]': 5,
          'filter[destination_warehouse_id]': this.me.current_warehouse,
          'filter[mitra_period_uuid]': this.filter.mitra_period_id,
          include: 'order',
          'fields[simple-orders]':
            'origin_code,destination_code,createdAt,order,order_status',
          'fields[orders]': 'grand_total_pv,grand_total_bv,origin_office_code',
          'filter[with_summary_cashbill]': true,
          'with_total_omzet': this.filter.is_omzet,
          ...this.filterStatus,
          ...params,
        },
      });

      if (!err) {
        this.orders = res;

        if (withSummary) {
          this.summary.totalPrice = res.meta.total_price
          this.summary.totalPv = res.meta.total_pv
          this.summary.totalBv = res.meta.total_bv
          this.summary.count = res.meta.page.total
          this.summary.totalOmzet = res.meta.total_omzet

          this.statusSummary = res.meta.status_summary
        }
      }

      this.loadingOrders = false;
    },
    async loadSummaryOrder() {
      this.loadingSummaryOrder = true;

      const [res, err] = await this.request('/api/v1/orders/-actions/summary', {
        params: {
          'filter[destination_warehouse_id]': this.me.current_warehouse,
          'filter[mitra_period_uuid]': this.filter.mitra_period_id,
        },
      });

      if (!err) {
        this.summaryOrder = res;
      }

      this.loadingSummaryOrder = false;
    },
  },
  activated() {
    this.filter.mitra_period_id = this.me.current_period.id;

    this.loadOrders();
    this.loadSummaryOrder();
  },
};
</script>
